// components/inputs/BaseSelect/BaseSelect.tsx

import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './BaseSelect.scss'

export interface BaseSelectOption {
  id: string
  label: string
}

export interface BaseSelectProps {
  label?: React.ReactNode
  labelStyles?: React.CSSProperties
  placeholder?: string
  options: BaseSelectOption[]
  value?: string
  onChange: (value: string, option: BaseSelectOption) => void
  onCreateNew?: (value: string) => void
  searchable?: boolean
  searchPlaceholder?: string
  error?: boolean
  disabled?: boolean
  required?: boolean
  backgroundColor?: string
  height?: string
  borderRadius?: string
  className?: string
  dataTest?: string
}

const BaseSelect: React.FC<BaseSelectProps> = ({
  label,
  labelStyles,
  placeholder = 'Select an option',
  options,
  value,
  onChange,
  onCreateNew,
  searchable = true,
  searchPlaceholder = 'Search...',
  error = false,
  disabled = false,
  required = false,
  backgroundColor,
  height,
  borderRadius,
  className,
  dataTest,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [customValue, setCustomValue] = useState('')
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const selectedOption = options.find((opt) => opt.id === value || opt.label === value)

  const displayValue = selectedOption?.label || customValue || value || ''

  const filteredOptions = options.filter((opt) => opt.label?.toLowerCase().includes(searchTerm.toLowerCase()))

  const hasExactMatch = options.some((opt) => opt.label?.toLowerCase() === searchTerm.toLowerCase())

  const commitSearchTerm = () => {
    if (searchTerm.trim()) {
      if (hasExactMatch) {
        const match = filteredOptions.find((opt) => opt.label.toLowerCase() === searchTerm.toLowerCase())
        if (match) {
          onChange(match.label, match)
          setCustomValue('')
        }
      } else if (onCreateNew) {
        onCreateNew(searchTerm.trim())
        setCustomValue(searchTerm.trim())
      }
    }
    setSearchTerm('')
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node) && !dropdownRef.current?.contains(e.target as Node) && isOpen) {
        const target = e.target as HTMLElement
        const isModalClose = target.closest('.modal__close')

        if (isModalClose) {
          setIsOpen(false)
          setSearchTerm('')
          return
        }

        setTimeout(() => {
          commitSearchTerm()
          setIsOpen(false)
        }, 0)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [searchTerm, hasExactMatch, onCreateNew, isOpen, filteredOptions])

  useEffect(() => {
    if (!isOpen) return

    const positionDropdown = () => {
      const trigger = triggerRef.current
      if (!trigger) return
      const rect = trigger.getBoundingClientRect()
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(80, window.innerHeight - rect.bottom - 12),
        zIndex: 4000,
      })
    }

    positionDropdown()
    window.addEventListener('resize', positionDropdown)
    window.addEventListener('scroll', positionDropdown, true)
    return () => {
      window.removeEventListener('resize', positionDropdown)
      window.removeEventListener('scroll', positionDropdown, true)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  useEffect(() => {
    if (selectedOption) {
      setCustomValue('')
    }
  }, [selectedOption])

  const handleSelect = (option: BaseSelectOption) => {
    onChange(option.label, option)
    setCustomValue('')
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleCreateNew = () => {
    if (onCreateNew && searchTerm.trim()) {
      onCreateNew(searchTerm.trim())
      setCustomValue(searchTerm.trim())
      setIsOpen(false)
      setSearchTerm('')
    }
  }

  return (
    <div ref={containerRef} className={`base-select-container ${className || ''}`} data-test={dataTest}>
      {label && (
        <div className="base-select-label" style={labelStyles}>
          {label}
          {required && <span className="base-select-required">*</span>}
        </div>
      )}

      <div
        ref={triggerRef}
        className={`base-select-trigger ${error ? 'error' : ''} ${disabled ? 'disabled' : ''} ${isOpen ? 'focused' : ''}`}
        onClick={() => {
          if (!disabled) {
            if (isOpen) {
              commitSearchTerm()
            }
            setIsOpen(!isOpen)
          }
        }}
        style={{
          backgroundColor: backgroundColor || 'var(--background-surface)',
          height: height || '46px',
          borderRadius: borderRadius || '8px',
        }}
      >
        <svg className="base-select-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9"
            stroke="#9CA3AF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {isOpen && searchable ? (
          <input
            ref={searchInputRef}
            type="text"
            placeholder={displayValue || searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="base-select-inline-input"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsOpen(false)
                setSearchTerm('')
              }
              if (e.key === 'Enter') {
                e.preventDefault()
                commitSearchTerm()
                setIsOpen(false)
              }
            }}
          />
        ) : (
          <span className={`base-select-value ${!displayValue ? 'placeholder' : ''}`}>{displayValue || placeholder}</span>
        )}
        <svg
          className={`base-select-chevron ${isOpen ? 'open' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path d="M4 6L8 10L12 6" stroke="#636F7E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {isOpen &&
        createPortal(
          <div ref={dropdownRef} className="base-select-dropdown" style={dropdownStyle}>
            <div className="base-select-options">
              {filteredOptions.map((option) => (
                <div
                  key={option.id}
                  className={`base-select-option ${value === option.id || value === option.label ? 'selected' : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </div>
              ))}

              {filteredOptions.length === 0 && !onCreateNew && <div className="base-select-empty">No results found</div>}

              {onCreateNew && searchTerm.trim() && !hasExactMatch && (
                <div className="base-select-option create-new" onClick={handleCreateNew}>
                  + Add "{searchTerm}"
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}

export default BaseSelect
